import React, { createContext, useContext, useEffect } from 'react';

import type { Course } from '@innovamat/radiance-utils';
import { roles, type Classroom } from '@innovamat/radiance-utils';
import { detectIsInIOS, detectIsInWebview } from '@innovamat/webview-bridge';
import { useQueryClient } from '@tanstack/react-query';
import { eventLogging } from './EventLogging';

type EventLoggingProviderProps = {
  children: React.ReactNode;
  variables: {
    ENVIRONMENT: string;
    // Solo Lambda API - todas las API keys ahora están en el servidor
    LAMBDA_API_URL?: string;
  };
};

type EventLoggingContextType = {
  setEventData: (
    eventName: string,
    eventData: Record<string, string | number | boolean | undefined | null>,
    addUserData?: boolean
  ) => void;
  // Método para verificar el estado de la API Lambda
  healthCheck: () => Promise<any>;
  // Métodos compatibles con la API original
  setDevice: (deviceId: string) => void;
  setIterableEmail: (email: string) => void;
  clear: () => void;
};

const EventLoggingContext = createContext<EventLoggingContextType>({
  setEventData: () => {},
  healthCheck: async () => ({ status: 'not-initialized' }),
  setDevice: () => {},
  setIterableEmail: () => {},
  clear: () => {},
});

const init = ({
  ENVIRONMENT,
  LAMBDA_API_URL,
}: EventLoggingProviderProps['variables']): void => {
  console.log('Initializing EventLogging (Lambda API only):', {
    environment: ENVIRONMENT,
    lambdaApiUrl: LAMBDA_API_URL || 'auto-detect',
  });

  eventLogging.init({
    isInWebview: detectIsInWebview(),
    isInIOS: detectIsInIOS(),
    isInLocal: ENVIRONMENT === 'local',
    // Solo configuración Lambda API
    lambdaApiUrl: LAMBDA_API_URL,
  });
};

type ClassroomQuery =
  | {
      classroom: Classroom;
    }
  | undefined;

type CoursesQuery =
  | {
      courses: Course[];
    }
  | undefined;

export const EventLoggingProvider = ({
  children,
  variables,
}: EventLoggingProviderProps) => {
  const queryClient = useQueryClient();

  const getUser = () => {
    return queryClient.getQueryData<any>(['User']);
  };

  const getCurrentClassroom = () => {
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.includes('classroom')
    ) {
      return undefined;
    }

    const pathname = window.location.pathname;
    const classroomMatch = pathname.match(/\/classroom\/([^/]+)/);
    const classroomId = classroomMatch ? classroomMatch[1] : null;

    if (!classroomId) {
      return undefined;
    }

    const classroomQueries = queryClient.getQueriesData<ClassroomQuery>({
      queryKey: ['Classroom'],
      exact: false,
    });

    if (!classroomQueries) {
      return null;
    }

    const availableClassroomQueries = classroomQueries.filter(
      (query) => query[1]?.classroom
    );

    for (const classroomQuery of availableClassroomQueries) {
      const classroom = classroomQuery[1]?.classroom;
      if (classroom && classroom.id === classroomId) {
        return classroom;
      }
    }

    return undefined;
  };

  const getCurrentCourse = () => {
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.includes('course')
    ) {
      return undefined;
    }

    const pathname = window.location.pathname;
    const courseMatch = pathname.match(/\/course\/([^/]+)/);
    const courseId = courseMatch ? courseMatch[1] : null;

    if (!courseId) {
      return undefined;
    }

    const coursesQuery = queryClient.getQueriesData<CoursesQuery>({
      queryKey: ['Courses'],
      exact: false,
    });

    if (!coursesQuery) {
      return null;
    }

    const availableCoursesQueries = coursesQuery.filter(
      (query) => query[1]?.courses
    );

    for (const courseQuery of availableCoursesQueries) {
      const courses = courseQuery[1]?.courses;
      if (courses) {
        const currentCourse = courses.find((course) => course.id === courseId);
        if (currentCourse) {
          return currentCourse.order;
        }
      }
    }

    return undefined;
  };

  useEffect(() => {
    init(variables);
  }, []);

  const setEventData = (
    eventType: string,
    eventProperties: any = {},
    addUserData = true
  ) => {
    const payload = { ...eventProperties };
    const currentClassroom = getCurrentClassroom();
    const user = getUser();

    if (addUserData) {
      if (user) {
        const role = user.roles ? user.roles?.join() : 'Not defined';
        const school_id = user.organizationId
          ? user.organizationId
          : 'Not defined';
        const { organizationName, crmId, locale, region, id } = user;
        payload.username = user?.email;
        payload.role = role;
        payload.school_id = school_id;
        payload.school_name = organizationName;
        payload.crm_id = crmId;
        payload.language = locale;
        payload.region = region;
        payload.user_uuid = id;

        if (currentClassroom) {
          payload.classroom_id = currentClassroom?.id;
          payload.course = currentClassroom?.courseOrder;
        }

        const currentCourse = getCurrentCourse();

        if (currentCourse) {
          payload.course = currentCourse;
        }

        if (roles.hasStudentRole(user.roles)) {
          payload.classroom_id = user.studentData?.classroomId;
          payload.course = user.studentData?.courseOrder;
        }

        if (user.isStudentViewActive) {
          payload.in_student_view = true;
        }
      }
    }

    payload.app_version = 'manager-1.0.0';

    if (addUserData && !user) return;
    eventLogging.log(eventType, payload);
  };

  const healthCheck = async () => {
    return await eventLogging.healthCheck();
  };

  const setDevice = (deviceId: string) => {
    eventLogging.setDevice(deviceId);
  };

  const setIterableEmail = (email: string) => {
    eventLogging.setIterableEmail(email);
  };

  const clear = () => {
    eventLogging.clear();
  };

  return (
    <EventLoggingContext.Provider
      value={{
        setEventData,
        healthCheck,
        setDevice,
        setIterableEmail,
        clear,
      }}
    >
      {children}
    </EventLoggingContext.Provider>
  );
};

export const useEventLogging = () => {
  const context = useContext(EventLoggingContext);
  if (context === null) {
    throw new Error(
      'useEventLogging must be used within an EventLoggingProvider'
    );
  }
  return context;
};
